import { ArmoryItem, Gear } from '../types/types';

/** Tag applied to all ATCC-imported items */
export const ATCC_TAG = 'ATCC Imported';
export const GENERATED_ATCC_SUFFIX = ' (Generated)';
export const WISHED_ATCC_SUFFIX = ' (Wished)';

export function normalizeATCCItemName(name: string, wished: boolean = false): string {
    const trimmedName = name.trim();
    const withoutGeneratedSuffix = trimmedName.endsWith(GENERATED_ATCC_SUFFIX)
        ? trimmedName.slice(0, -GENERATED_ATCC_SUFFIX.length)
        : trimmedName;

    if (wished || trimmedName.endsWith(GENERATED_ATCC_SUFFIX)) {
        return `${withoutGeneratedSuffix}${WISHED_ATCC_SUFFIX}`;
    }

    return trimmedName;
}

export function isATCCItem(item: ArmoryItem): boolean {
    const tags = item.tags ?? [];
    return tags.includes(ATCC_TAG);
}

export function normalizeATCCItem<T extends ArmoryItem>(item: T): T {
    const tags = item.tags ?? [];
    const normalizedName = normalizeATCCItemName(item.name);
    const normalizedTags = tags.includes(ATCC_TAG) ? tags : [...tags, ATCC_TAG];

    return {
        ...item,
        name: normalizedName,
        tags: normalizedTags,
    };
}

/**
 * Merges new ATCC items into an existing item list.
 * 1. Removes all existing ATCC items
 * 2. Combines remaining manual items with the new ATCC items
 * 3. Sorts the result alphabetically by name using locale-aware comparison
 */
export function mergeATCCItems(existingItems: ArmoryItem[], newATCCItems: ArmoryItem[]): ArmoryItem[] {
    const manualItems = existingItems.filter((item) => !isATCCItem(item));
    const merged = [...manualItems, ...newATCCItems.map((item) => normalizeATCCItem(item))];
    return merged.sort((a, b) => a.name.localeCompare(b.name));
}

/** @deprecated Use mergeATCCItems instead */
export function mergeATCCGear(existingGear: Gear[], newATCCItems: ArmoryItem[]): Gear[] {
    return mergeATCCItems(existingGear, newATCCItems);
}

/**
 * Removes all ATCC-imported items from an item list.
 * Returns only the manually created items.
 */
export function deleteATCCItems(existingItems: ArmoryItem[]): ArmoryItem[] {
    return existingItems.filter((item) => !isATCCItem(item));
}

/** @deprecated Use deleteATCCItems instead */
export function deleteATCCGear(existingGear: Gear[]): Gear[] {
    return deleteATCCItems(existingGear);
}
